<template>
  <div class="theme-dark gd-library-bar">
    <div class="gd-library-titlebar">
      <button type="button" class="gd-library-close" @click.prevent="closeClicked" v-if="false">
        <i class="far fa-times"></i>
      </button>
      {{title}}
    </div>
    <div class="gd-library-container">
      <div class="gd-library-content">

        <div class="btn-group gd-library-type-button-group">
          <button :class="['btn', 'btn-default', {'active': isFilterModeStandard}]" @click.prevent="filterMode = LibraryItemFilterTypes.standard" v-if="showFilterModeStandard">Standard</button>
          <button :class="['btn', 'btn-default', {'active': isFilterModeCustomerTemplate}]" @click.prevent="filterMode = LibraryItemFilterTypes.customerTemplate" v-if="showCustomerTemplates">Customer Templates</button>
          <button :class="['btn', 'btn-default', {'active': isFilterModeAllTemplate}]" @click.prevent="filterMode = LibraryItemFilterTypes.allTemplate" v-if="showAllTemplates">All Templates</button>
        </div>

        <div v-if="displayAddNewItem" class="my-2">
          <button class="btn btn-block btn-primary" @click.prevent="addItemClicked"><i class="far fa-plus" /> Add Item</button>
        </div>

        <!-- Paginated Item list -->
        <div class="gd-library-filterbar mt-2" v-show="isFilterModeCustomerTemplate || isFilterModeAllTemplate">
          <div class="row no-gutters">
            <div class="col-10">
              <FormInputText placeholder="Search" v-model="searchText" />
            </div>
            <div class="col-2 pl-2">
              <FilterInput ref="filterInput" :filterOptions="filterList" v-model="libraryItemLabelFilter" :showSelected="false" />
            </div>
          </div>
          <Spinner v-if="libraryLoading" />
        </div>

        <!-- Standard Items bundled into the editor -->
        <div ref="libraryItemListContainer">
          <EditorLibraryItem 
            v-for="item of libraryItems"
            :key="item.id"
            :libraryItemId="item.id"
            :title="item.name"
            :description="item.description"
            :labelList="item.labelList"
            :contactIdList="item.contactIdList"
            :icon="item.icon"
            :iconClass="item.iconClass"
            :thumbnailData="item.thumbnailImageData"
            :itemType="item.type"
            :info="item"
            @click="libraryItemClicked(item)"
          />
        </div>
      </div>
    </div>
    <div class="gd-library-footerbar px-2">
      <div class="row no-gutters gd-library-page-area" v-show="!isFilterModeStandard">
        <div class="col-3">
          <button class="btn btn-block btn-default" @click.prevent="setPageIndex(libraryCurrentPage-1)" :disabled="libraryCurrentPage == 0"><i class="far fa-angle-left fa-fw"></i></button>
        </div>
        <div class="col-6 gd-library-page-area-display">
          {{libraryCurrentPage+1}} / {{libraryPageCount}}
        </div>
        <div class="col-3">
          <button class="btn btn-block btn-default" @click.prevent="setPageIndex(libraryCurrentPage+1)" :disabled="libraryCurrentPage == libraryPageCount-1"><i class="far fa-angle-right fa-fw"></i></button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$editor-library-titlebar-height: 30px;
$editor-library-filterbar-height: 50px;
$editor-library-footer-height: 46px;

.gd-library-bar {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border-right: 1px solid #a0a0a0;
  user-select: none;
  padding: 0;
}
.gd-library-titlebar {
  width: 100%;
  height: $editor-library-titlebar-height;
  background-color: #415472;
  color: #e4e4e7;
  line-height: $editor-library-titlebar-height;
  font-weight: bold;
  font-size: 15px;
  text-align: center;
  vertical-align: middle;
}
.gd-library-footerbar {
  position: absolute;
  height: $editor-library-footer-height;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $editor-sidebar-background-color;
}
.gd-library-container {
  position: absolute;
  top: $editor-library-titlebar-height;
  left: 0;
  right: 0;
  bottom: $editor-library-footer-height;
  overflow-y: auto;
  background-color: $editor-sidebar-background-color;
}
.gd-library-content {
  padding: 6px;
}
.gd-library-filterbar {
  display: block;
  clear: both;
}

.gd-library-close {
  cursor: pointer;
  float: right;
  border: 0;
  background-color: transparent;
  padding: 1.25rem 1.5rem 1rem 1rem;
  margin: -1rem -1rem -1rem auto;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.5rem;
  color: #5891c2;
}
.gd-library-type-button-group > .btn {
  font-size: 0.70rem;
  background: transparent;
  box-shadow: none;
  color: #e4e4e7;
  border: none;
  border-radius: 0;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.gd-library-type-button-group > .btn.active {
  color: #68ACE8;
  border-bottom: 2px solid #68ACE8;
  background: #2C2C2F;
}

.gd-library-footerbar button {
  height: calc(#{$editor-library-footer-height} - 10px);
  padding-top: 4px;
  padding-bottom: 4px;
}
.gd-library-page-area {
  font-size: 1.25rem;
  padding-top: 5px;
}
.gd-library-page-area-display {
  line-height: calc(#{$editor-library-footer-height} - 10px);
  vertical-align: middle;
  text-align: center;
  color: #d7d7d7;
}
</style>

<script>
import _ from 'lodash'
import { mapState } from "vuex"
import dragula from 'dragula'
import 'dragula/dist/dragula.css'

import FilterInput from '@/components/filter/filterInput'
import EditorLibraryItem from './editorLibraryItem'

import { LibraryItemTypes, LibraryItemFilterTypes } from '@glass-designer/types'

export default {
  name: 'EditorLibrarySideOverlay',
  components: {
    FilterInput,
    EditorLibraryItem
  },
  props: {
    title: String,
    itemTypeFilter: String,
    contactId: [Number, String],
  },
  data() {
    return {
      searchText: '',
      filterMode: LibraryItemFilterTypes.standard,
      libraryItemLabelFilter: [],
      libraryLoading: true,
      libraryLoadingError: '',
      libraryListData: {},

      // Pagination info
      libraryPageCount: 0,
      libraryOffset: 0,
      libraryPageLength: 10,
      libraryCurrentPage: 0,
      libraryCurDraw: 1,
      libraryRecvDraw: 0
    }
  },
  computed: {
    ...mapState({
      builtInLibraryList: state => state.apps.editorLibrary.itemList
    }),
    libraryItems() {
      if(this.filterMode == LibraryItemFilterTypes.standard)
        return this.builtInLibraryList.filter(item => item.type == this.itemTypeFilter)

      return this.libraryListData.data
    },
    filterList() {
      var itemFilterOptions = []

      let libItemLabels = this.$store.getters['label/getLabelsByType']('orderEditorLibraryItem')
      itemFilterOptions.push({
        name: 'Library Item Labels',
        field: 'labelList',
        options: libItemLabels.map(labelInfo => {
          return {
            name: labelInfo.name,
            value: labelInfo.id,
            colour: labelInfo.colour
          }
        })
      })

      return itemFilterOptions
    },
    displayAddNewItem() {
      return this.itemTypeFilter == LibraryItemTypes.panelItem
    },
    isFilterModeStandard() {
      return this.filterMode == LibraryItemFilterTypes.standard
    },
    isFilterModeCustomerTemplate() {
      return this.filterMode == LibraryItemFilterTypes.customerTemplate
    },
    isFilterModeAllTemplate() {
      return this.filterMode == LibraryItemFilterTypes.allTemplate
    },
    showFilterModeStandard() {
      return this.itemTypeFilter != LibraryItemTypes.panelItem && this.itemTypeFilter != LibraryItemTypes.kit
    },
    showCustomerTemplates() {
      return true
    },
    showAllTemplates() {
      return true
    }
  },
  watch: {
    itemTypeFilter: function() {
      if(!this.showFilterModeStandard)
        this.filterMode = LibraryItemFilterTypes.customerTemplate
      else
        this.filterMode = LibraryItemFilterTypes.standard

      // Clear info
      this.libraryItemLabelFilter = []
      this.$refs.filterInput.clearFilters()
      this.searchText = ''

      this.clearLibraryList()
      this.libraryLoading = true
      this.debouncedNavigateLibraryList()
    },
    libraryItemLabelFilter: function() {
      this.clearLibraryList()
      this.libraryLoading = true
      this.debouncedNavigateLibraryList()
    },
    filterMode: function() {
      this.clearLibraryList()

      if(!this.isFilterModeStandard) {
        this.libraryLoading = true
        this.libraryItemLabelFilter = []
        this.$refs.filterInput.clearFilters()
        this.debouncedNavigateLibraryList()
      }
    },
    searchText: function() {
      this.libraryCurrentPage = 0
      this.debouncedSearchLibraryList()
    }
  },
  created() {
    this.LibraryItemFilterTypes = LibraryItemFilterTypes

    this.debouncedSearchLibraryList = _.debounce(this.requestLibraryList, 250)
    this.debouncedNavigateLibraryList = _.debounce(this.requestLibraryList, 100)
  },
  mounted() {
    if(!this.showFilterModeStandard) {
      this.filterMode = LibraryItemFilterTypes.customerTemplate
      this.libraryLoading = true
      this.requestLibraryList()
    }

    this.requestFilterLabels()
    .then(() => {
    })
    .catch(err => {})
    
    var overlayComponent = this
    this.$options.drake = dragula([this.$refs.libraryItemListContainer], {
      copy: true,
      removeOnSpill: true,
      slideFactorX: 5,
      slideFactorY: 5,
      isContainer: function(el) {
        if(el.classList.contains('gd-library-editor-droppable'))
          return true

        return false
      },
      moves: function (el, source, handle, sibling) {
        return el.classList.contains('gd-library-item-draggable');
      },
      accepts: function (el, target, source, sibling) {
        // If over the editor, convert into a draggable object in the THREE scene
        if(target.classList.contains('gd-library-editor-droppable')) {
          overlayComponent.$options.drake.cancel(true)

          // Notify editor to start the tool & drag the object
          let libraryItemData = overlayComponent.libraryItems.find(item => item.id == el.dataset.itemid)
          if(libraryItemData)
            overlayComponent.$emit('libraryItemDraggedToEditor', libraryItemData)
        }
        return false
      }
    })
  },
  methods: {
    closeClicked() {
      this.$emit('close')
    },
    libraryItemClicked(item) {
      this.$emit('libraryItemClicked', item)
    },
    addItemClicked() {
      this.$emit('libraryAddItemClicked')
    },

    // Get the label info for the filter popovers used by the library
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {
          let libraryItemLabels = await this.$apiService.coreService.getLabelsForType('orderEditorLibraryItem')
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'orderEditorLibraryItem',
            labelList: libraryItemLabels.data
          })

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    },

    reloadData() {
      this.requestLibraryList()
    },

    clearLibraryList() {
      this.libraryCurrentPage = 0
      this.libraryListData = {}
    },

    // Request the library list
    requestLibraryList() {
      this.libraryOffset = this.libraryCurrentPage * this.libraryPageLength
      this.libraryListData = {}

      var dataTableParams = {
        start: this.libraryOffset,
        length: this.libraryPageLength,
        draw: this.libraryCurDraw++,
        'search[value]': this.searchText,
        'search[regex]': false,
        type: this.itemTypeFilter,
        format: 'datatable'
      }
      if(this.filterMode == LibraryItemFilterTypes.customerTemplate)
        dataTableParams.contactId = parseInt(this.contactId || 0)

      if(this.libraryItemLabelFilter && this.libraryItemLabelFilter.length > 0) {
        dataTableParams.labelIdList = this.libraryItemLabelFilter.map(label => label.value).join(',')
      }

      this.libraryLoadingError = ''
      let url = `/ordereditor/libraryitems?` + this.buildQueryParams(dataTableParams)
      this.$api.get(url)
      .then(response => {

        // Discard results if the data is older than newer data we have
        if(parseInt(response.data.draw) >= this.libraryRecvDraw) {
          this.libraryListData = response.data

          this.libraryPageCount = Math.max(Math.ceil(this.libraryListData.recordsFiltered / this.libraryPageLength), 1)

          this.libraryCurrentPage = this.libraryOffset / this.libraryPageLength
          this.libraryCurrentPage = Math.min(this.libraryCurrentPage, this.libraryPageCount-1)
          this.libraryCurrentPage = Math.max(this.libraryCurrentPage, 0)

          this.libraryRecvDraw = parseInt(response.data.draw)
          this.libraryCurDraw = Math.max(this.libraryCurDraw, this.libraryRecvDraw)
        }
      })
      .catch(err => {
        this.libraryLoadingError = 'Error loading library'
      })
      .finally(() => {
        this.libraryLoading = false
      })
    },

    setPageIndex(index) {
      if(index < 0 || index >= this.libraryPageCount)
        return

      this.libraryCurrentPage = index
      this.debouncedNavigateLibraryList()
    },
  }

}
</script>